import React from "react";
import { useTranslation } from "react-i18next";
import { HiCheck } from "react-icons/hi";

import NoTranslate from "../../../atoms/NoTranslate";
import AddToGoogleCalendarButton from "../../../atoms/AddToGoogleCalendarButton";
import { CustomButton } from "../../../atoms/CustomButton";
import type { Appointment } from "../../../../hooks/agenda";
import { useBookingMoment } from "./BookingSlotPicker";
import {
  browserTimeZone,
  dayLabel,
  rangeLabel,
} from "../../dashboard/agenda/agendaDates";

interface BookingConfirmationProps {
  appointment: Appointment & { ics_url?: string | null };
  withName: string;
  /** The service booked, named for the client; none hides its row. */
  serviceTitle?: string;
  calendarTitle: string;
  children?: React.ReactNode;
}

export const BookingConfirmation: React.FC<BookingConfirmationProps> = ({
  appointment,
  withName,
  serviceTitle,
  calendarTitle,
  children,
}) => {
  const { t } = useTranslation("clientView");
  const { at } = useBookingMoment();
  const startAt = at(appointment.start);
  const endAt = at(appointment.end);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <HiCheck size={24} className="text-green-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900">
          {t("customRegistration.page.booking.confirmedTitle")}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {t("customRegistration.page.booking.confirmedSubtitle")}
        </p>
      </div>

      <div className="space-y-2">
        <dl className="grid grid-cols-[7.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1.5 rounded-lg border border-gray-200 p-4 text-sm">
          {serviceTitle && (
            <>
              <dt className="text-gray-500">
                {t("customRegistration.page.booking.serviceLabel")}
              </dt>
              <dd className="break-words font-medium text-gray-900">
                <NoTranslate>{serviceTitle}</NoTranslate>
              </dd>
            </>
          )}
          <dt className="text-gray-500">
            {t("customRegistration.page.booking.dateLabel")}
          </dt>
          <dd className="text-gray-900">{dayLabel(startAt)}</dd>
          <dt className="text-gray-500">
            {t("customRegistration.page.booking.timeLabel")}
          </dt>
          <dd className="text-gray-900 tabular-nums">
            {rangeLabel(t, "time", startAt.format("LT"), endAt.format("LT"))}
          </dd>
          <dt className="text-gray-500">
            {t("customRegistration.page.booking.withLabel")}
          </dt>
          <dd className="break-words text-gray-900">
            <NoTranslate>{withName}</NoTranslate>
          </dd>
          {appointment.location && (
            <>
              <dt className="text-gray-500">
                {t("customRegistration.page.booking.locationLabel")}
              </dt>
              <dd className="text-gray-900 break-words">
                <NoTranslate>{appointment.location}</NoTranslate>
              </dd>
            </>
          )}
        </dl>
        <p className="text-xs text-gray-500">
          {t("customRegistration.page.booking.timezoneHint", {
            timezone: browserTimeZone(),
          })}
        </p>
      </div>

      <div className="space-y-3">
        <AddToGoogleCalendarButton
          title={calendarTitle}
          description={appointment.description}
          location={appointment.location}
          startDateTime={new Date(appointment.start).toISOString()}
          endDateTime={new Date(appointment.end).toISOString()}
          buttonText={
            t("customRegistration.page.booking.addToCalendar") as string
          }
          variant="secondary"
          className="w-full"
        />
        {appointment.ics_url && (
          <CustomButton
            href={appointment.ics_url}
            variant="secondary"
            icon="download"
            title={t("customRegistration.page.booking.addToCalendarIcs")}
            className="w-full"
          />
        )}
        {children}
      </div>
    </div>
  );
};

export default BookingConfirmation;
